
import { ElMessage } from 'element-plus';
import { defineComponent, ref, watch, onMounted, onUnmounted } from 'vue';
import * as monaco from 'monaco-editor';
import { uploadFile } from '@/api/open';

export default defineComponent({
  name: 'MonacoEditor',
  props: {
    options: {
      type: Object,
      default: () => {
        return {
          language: 'json',
        };
      },
    },
    modelValue: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const refEditor = ref();
    let monacoEditor: monaco.editor.IStandaloneCodeEditor;
    let isUpdating = false;

    // 更新编辑器只读状态的方法
    const updateReadOnlyState = (readOnly: boolean) => {
      if (monacoEditor) {
        monacoEditor.updateOptions({ readOnly });
        // 同时更新整体选项保证下次创建时状态正确
        monacoEditor.updateOptions({ ...props.options, readOnly });
      }
    };

    // 文件上传处理
    const handleFileUpload = async (): Promise<string> => {
      return new Promise((resolve, reject) => {
        // 创建隐藏的file input
        const input = document.createElement('input');
        input.type = 'file';
        input.style.display = 'none';
        input.multiple = true;
        input.onchange = async (e: Event) => {
          const target = e.target as HTMLInputElement;
          const { files } = target;

          if (!files || files.length === 0) {
            reject('未选择文件');
            return;
          }

          try {
            const formData = new FormData();
            Array.from(files).forEach((file) => {
              formData.append('files', file);
            });
            const { data } = await uploadFile(formData);
            const urls = data.map(item => item.url).filter(url => url).join(';');
            resolve(urls); // 根据实际API响应结构调整
          } catch (error) {
            ElMessage.error('文件上传失败');
            reject(error);
          } finally {
            document.body.removeChild(input);
          }
        };

        document.body.appendChild(input);
        input.click();
      });
    };

    // 添加上传菜单项
    const addContextMenu = () => {
      monacoEditor.addAction({
        id: 'upload-file',
        label: '上传文件插入URL',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 0,
        run: async () => {
          if (props.readonly) {
            ElMessage.warning('只读模式下不可编辑');
            return;
          }

          try {
            const url = await handleFileUpload();
            const position = monacoEditor.getPosition();
            const range = new monaco.Range(
              position!.lineNumber,
              position!.column,
              position!.lineNumber,
              position!.column,
            );

            monacoEditor.executeEdits('', [{
              range,
              text: url,
              forceMoveMarkers: true,
            }]);
          } catch (error) {
            console.error('上传失败:', error);
          }
        },
      });
    };

    onMounted(() => {
      // 合并只读状态到编辑器配置
      const editorOptions = {
        value: props.modelValue,
        tabSize: 2,
        theme: 'vs-dark',
        automaticLayout: true,
        readOnly: props.readonly,
        formatOnPaste: true,
        formatOnType: true,
        ...props.options,
      };
      // 初始化时直接创建编辑器
      monacoEditor = monaco.editor.create(refEditor.value, editorOptions);
      addContextMenu();
      // 绑定内容变化监听
      monacoEditor.onDidChangeModelContent(() => {
        if (isUpdating) return;
        emit('update:modelValue', monacoEditor.getValue());
      });

      // 监听只读状态变化
      watch(() => props.readonly,
        (newVal) => {
          updateReadOnlyState(newVal);
        });

      // 监听外部传入的值变化
      watch(
        () => props.modelValue,
        (newValue) => {
          if (monacoEditor && newValue !== monacoEditor.getValue()) {
            isUpdating = true; // 标记正在外部更新
            monacoEditor.setValue(newValue);
            isUpdating = false;
          }
        },
      );
    });

    onUnmounted(() => {
      monacoEditor?.dispose();
    });

    return {
      refEditor,
    };
  },
});
